// Loads a cover into an offscreen canvas, downsamples it, and extracts a
// small set of dominant colors + brightness/saturation/warmth for
// StageLightRig (and later NeonPulse/ParticleStorm/Geometry) to key off.
//
// CORS note: this deliberately goes through /api/art-proxy (same origin
// as this page) rather than the raw Sonos absoluteAlbumArtUri. The
// speaker's own embedded HTTP server sends no CORS headers, so a canvas
// drawn from that URL directly would be "tainted" and getImageData would
// throw a SecurityError - this is the exact problem tv.js's mesh
// background hit and solved with the same proxy route (see
// kiosk-app/src/routes/api.js). Reusing that already-proven path here
// rather than re-deriving a workaround.

import { API_BASE } from './api-base.js';

const DOWNSAMPLE = 16;
const QUANT_STEP = 32; // bucket width per channel when finding dominant colors

// dominantColors used to be just the top 4 buckets by pixel count, which
// often means 4 near-identical shades of whatever color dominates the
// photo (e.g. four shades of sky) rather than colors that actually read
// as different on the rig - the link between "pochette" and "projecteurs"
// was there but weak. This now greedily picks CONTRASTED_COUNT colors
// that are both frequent AND spread apart in RGB space (see
// pickContrastedColors), so the 5 colors driving the show are the ones
// that actually look different from each other on the cover.
//
// vividColors then boosts each of those real colors toward "party"
// saturation/lightness rather than replacing them with a synthetic
// scheme - a real link to the artwork's actual color relationships, not
// just one anchor hue. Only when the cover has no real hue to work with
// (B&W/near-grayscale - checked via the *most* saturated of the 5, not
// the image average, since a mostly-neutral cover with one strong color
// patch should still count as "has color") does it fall back to a fully
// synthetic vivid palette, evenly spread across 5 hues anchored on a
// hash of the artwork URL (deterministic per cover, not random).
const CONTRASTED_COUNT = 5;
const CONTRAST_CANDIDATE_POOL = 16; // buckets considered before picking the 5 most contrasted
const VIVID_SATURATION = 0.88;
const VIVID_LIGHTNESS = 0.56;
const VIVID_HUE_OFFSETS_5 = [0, 72, 144, 216, 288]; // even 5-way spread, B&W fallback only
const GRAYSCALE_SATURATION_THRESHOLD = 0.12; // below this, treat the cover as hueless

function artProxyUrl(artUrl) {
  // Une pochette déjà embarquée (data-URL : c'est ainsi que l'extension
  // rapporte celles des enceintes Sonos) n'a rien à demander au relais.
  if (/^data:/i.test(artUrl)) return artUrl;
  return API_BASE + '/api/art-proxy?url=' + encodeURIComponent(artUrl);
}

function toHex(r, g, b) {
  return (1 << 24) | (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s: s, l: l };
}

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r, g, b;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  return toHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

// Deterministic (not random) so the same cover always gets the same
// fallback hue across replays/reloads - djb2, same approach as
// sonos-visual-profile.js's trackId hash, just kept local rather than
// importing across these otherwise-independent modules.
function hashHue(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

function colorDistance(hexA, hexB) {
  const dr = ((hexA >> 16) & 0xff) - ((hexB >> 16) & 0xff);
  const dg = ((hexA >> 8) & 0xff) - ((hexB >> 8) & 0xff);
  const db = (hexA & 0xff) - (hexB & 0xff);
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

// Greedy farthest-color pick: always keeps the most frequent color first
// (so [0] is a real "dominant color", not just "most contrasted"), then
// adds the most frequent remaining candidate that's still at least
// `threshold` away (in RGB space) from everything already picked,
// relaxing the threshold if the cover doesn't have enough spread to fill
// `count` colors that way. Falls back to padding with the next most
// frequent colors regardless of distance for near-flat covers.
function pickContrastedColors(sortedColors, count) {
  const picked = [sortedColors[0]];
  for (let threshold = 90; picked.length < count && threshold > 10; threshold -= 15) {
    for (let i = 1; i < sortedColors.length && picked.length < count; i++) {
      const candidate = sortedColors[i];
      if (picked.indexOf(candidate) !== -1) continue;
      const farEnough = picked.every(function (p) { return colorDistance(p, candidate) >= threshold; });
      if (farEnough) picked.push(candidate);
    }
  }
  let i = 0;
  while (picked.length < count && i < sortedColors.length) {
    if (picked.indexOf(sortedColors[i]) === -1) picked.push(sortedColors[i]);
    i++;
  }
  while (picked.length < count) picked.push(picked[picked.length - 1] || 0x808080);
  return picked;
}

// Pushes a real extracted color toward "party" saturation/lightness
// without discarding its actual hue - multiplicative saturation boost
// only (no additive floor), so a genuinely neutral bucket (e.g. white
// text on a colorful cover) stays neutral instead of getting a
// fabricated hue, while any bucket with real color signal reads as
// clearly more vivid on the rig than the flat photo color would.
function boostColor(hex) {
  const hsl = rgbToHsl((hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff);
  const s = Math.min(1, hsl.s * 1.8);
  const l = Math.min(0.62, Math.max(0.38, hsl.l));
  return hslToHex(hsl.h, s, l);
}

function buildVividColors(contrastedColors, seedKey) {
  let maxSaturation = 0;
  contrastedColors.forEach(function (hex) {
    const hsl = rgbToHsl((hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff);
    if (hsl.s > maxSaturation) maxSaturation = hsl.s;
  });
  if (maxSaturation < GRAYSCALE_SATURATION_THRESHOLD) {
    // B&W/near-grayscale cover - no real hue anywhere in the 5 colors to
    // work with, so fall back to a fully synthetic vivid spread anchored
    // on a hash of the artwork URL (deterministic per cover, not random).
    const anchorHue = hashHue(seedKey);
    return VIVID_HUE_OFFSETS_5.map(function (offset) {
      return hslToHex(anchorHue + offset, VIVID_SATURATION, VIVID_LIGHTNESS);
    });
  }
  return contrastedColors.map(boostColor);
}

function extractFromImageData(data, seedKey) {
  const buckets = new Map();
  let sumLuma = 0;
  let sumSat = 0;
  let sumWarmth = 0;
  const pixelCount = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    const key =
      (Math.floor(r / QUANT_STEP) << 10) |
      (Math.floor(g / QUANT_STEP) << 5) |
      Math.floor(b / QUANT_STEP);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { count: 0, r: 0, g: 0, b: 0 };
      buckets.set(key, bucket);
    }
    bucket.count++;
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    sumLuma += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    sumSat += max === 0 ? 0 : (max - min) / max;
    sumWarmth += (r - b) / 255; // -1 (cool/blue) .. 1 (warm/red-orange)
  }

  const sortedBuckets = Array.from(buckets.values()).sort(function (a, b) { return b.count - a.count; });
  const candidateColors = sortedBuckets.slice(0, CONTRAST_CANDIDATE_POOL).map(function (bucket) {
    return toHex(bucket.r / bucket.count, bucket.g / bucket.count, bucket.b / bucket.count);
  });
  const dominantColors = pickContrastedColors(candidateColors, CONTRASTED_COUNT);

  return {
    dominantColors: dominantColors,
    vividColors: buildVividColors(dominantColors, seedKey),
    brightness: sumLuma / pixelCount,
    saturation: sumSat / pixelCount,
    warmth: sumWarmth / pixelCount
  };
}

// Resolves to a palette object, or rejects - callers should treat a
// rejection as "no palette this track" and fall back to defaults (see
// StageLightRig._resolvePalette), not as fatal.
export function extractArtworkPalette(artUrl) {
  return new Promise(function (resolve, reject) {
    if (!artUrl) {
      reject(new Error('no artwork url'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = DOWNSAMPLE;
        canvas.height = DOWNSAMPLE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, DOWNSAMPLE, DOWNSAMPLE);
        const data = ctx.getImageData(0, 0, DOWNSAMPLE, DOWNSAMPLE).data;
        resolve(extractFromImageData(data, artUrl));
      } catch (err) {
        // A SecurityError here means the canvas got tainted (CORS) -
        // surfaced to the caller rather than swallowed, per the brief.
        reject(err);
      }
    };
    img.onerror = function () {
      reject(new Error('artwork failed to load'));
    };
    img.src = artProxyUrl(artUrl);
  });
}
